import {
  type ReactNode, useCallback, useState,
} from 'react';
import {
  computeDistanceBetween, computeHeading,
} from 'spherical-geometry-js';
import type { UnitSystem } from '~/_shared/types/googleMaps/googleMaps.types';
import type { LatLng } from '~/_shared/types/latLng';
import { createUuid } from '~/_shared/utils/createUuid';
import { getDistanceLabel } from '~/directions/listing/directionsListing.helpers';
import type { MapObjectZIndex } from '../mapObject.types';
import { MapCircleMetricContainer } from './mapCircleMetric.container';
import type { MapCircleInstance } from './mapCircleModel';
import { MapCirclePixelContainer } from './mapCirclePixel.container';
import { useDrawCircle } from './useDrawCircle.hook';

export const MapCircleDrawContainer = ({
  units,
  maxPixels,
  minPixels,
  zIndex,
  unitSystem,
  showDistanceFromCenterToCursor,
  renderCenter,
  renderCircle,
  onCircleDrawn,
}: {
  units: CircleAreaUnit;
  minPixels?: number;
  maxPixels?: number;
  zIndex: MapObjectZIndex;
  unitSystem: UnitSystem;
  showDistanceFromCenterToCursor?: boolean;
  renderCircle?: (key: string, circle: MapCircleInstance) => ReactNode;
  renderCenter?: (key: string, center: LatLng) => ReactNode;
  onCircleDrawn: (params: { center: LatLng; radiusInPixels: number; radiusInMeters: number }) => void;
}) => {
  const [circleInstanceId] = useState(createUuid());

  const {
    center: circleCenter,
    radiusInPixels: temporaryCircleRadiusInPx,
    radiusInMeters: temporaryCircleRadiusInMeters,
  } = useDrawCircle({
    isActive: true,
    minPixels,
    maxPixels,
    onCircleDrawn,
  });

  const getCursorLabel = useCallback((cursorLocation: LatLng) => {
    if (!circleCenter || !showDistanceFromCenterToCursor) {
      return null;
    }

    const heading = computeHeading(circleCenter, cursorLocation);
    const angle = heading < 0 ? heading + 360 : heading;

    return {
      text: getDistanceLabel(
        computeDistanceBetween(circleCenter, cursorLocation),
        unitSystem,
      ),
      angle,
    };
  }, [circleCenter, unitSystem, showDistanceFromCenterToCursor]);

  if (!circleCenter || !temporaryCircleRadiusInPx || !temporaryCircleRadiusInMeters) {
    return null;
  }

  if (units === 'metric') {
    return (
      <MapCircleMetricContainer
        circle={{
          id: circleInstanceId,
          lat: circleCenter.lat,
          lng: circleCenter.lng,
          radius: temporaryCircleRadiusInMeters / 1000,
          units: 'metric',
        }}
        zIndex={zIndex}
        getCursorLabel={getCursorLabel}
        renderCircle={renderCircle}
        renderCenter={renderCenter}
      />
    );
  }
  else {
    return (
      <MapCirclePixelContainer
        circle={{
          id: circleInstanceId,
          lat: circleCenter.lat,
          lng: circleCenter.lng,
          strokeWeight: 0, // not needed when outlines are not rendered
          radius: temporaryCircleRadiusInPx,
          units: 'pixels',
        }}
        zIndex={zIndex}
        getCursorLabel={getCursorLabel}
        renderCircle={renderCircle}
        renderCenter={renderCenter}
      />
    );
  }
};
